import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlay, FaUsers, FaHeart, FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { NEWS, formatNewsDate, newsImageUrl, newsKicker, newsText } from '../data/news';
import './Hero.css';

/** Koliko vesti hero prikazuje: jedna istaknuta + ostale kao lista. */
const HERO_NEWS_COUNT = 3;

const Hero: React.FC = () => {
  const { t, language } = useLanguage();
  // Računa se odmah (ne u efektu), da animacija ne krene sa opacity 0 pa ostane zaglavljena
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Na telefonu bez animacije ulaska (brži prikaz sadržaja)
  const enter = (delay: number, y = 20) => ({
    initial: isMobile ? (false as const) : { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    transition: isMobile ? { duration: 0 } : { duration: 0.7, delay },
  });

  const [featured, ...rest] = NEWS.slice(0, HERO_NEWS_COUNT);

  return (
    <section id="home" className={`hero hero--cup${featured ? ' hero--with-news' : ''}`}>
      <div
        className="hero__background"
        style={{
          ['--hero-bg-desktop' as string]: `url(${process.env.PUBLIC_URL}/images/main.jpg)`,
          ['--hero-bg-mobile' as string]: `url(${process.env.PUBLIC_URL}/images/main-mobile.jpg)`,
        } as React.CSSProperties}
      >
        <div className="hero__image-overlay"></div>
      </div>

      <div className="hero__inner">
        <div className="hero__main">
          <motion.p className="hero__eyebrow" {...enter(0.1, 12)}>
            <span className="hero__eyebrow-dot" aria-hidden />
            {t('hero.eyebrow')}
          </motion.p>

          <motion.h1 className="hero__title" {...enter(0.2)}>
            {t('hero.h1')}
            <span className="hero__title-sub">{t('hero.h1Sub')}</span>
          </motion.h1>

          <motion.ul className="hero__taglines" {...enter(0.35)}>
            <li className="hero__tagline-item">{t('hero.tagline1')}</li>
            <li className="hero__tagline-item">{t('hero.tagline2')}</li>
            <li className="hero__tagline-item">{t('hero.tagline3')}</li>
          </motion.ul>

          <motion.div className="hero__buttons" {...enter(0.5)}>
            <a className="btn-primary" href="/upis/">
              <FaUsers className="btn-icon" />
              {t('hero.joinUs')}
            </a>
            <button className="btn-primary btn-secondary" onClick={() => scrollToSection('training-experience')}>
              <FaPlay className="btn-icon" />
              {t('hero.viewTeam')}
            </button>
          </motion.div>
        </div>

        {featured && (
          <motion.aside className="hero-news" aria-labelledby="hero-news-title" {...enter(0.45, 28)}>
            <div className="hero-news__head">
              <h2 id="hero-news-title" className="hero-news__title">
                {t('hero.latestNews')}
              </h2>
              <a className="hero-news__all" href="/vesti/">
                {t('hero.allNews')} <FaArrowRight aria-hidden />
              </a>
            </div>

            <a className="hero-news__featured" href={`/vesti/${featured.slug}/`}>
              <img
                src={newsImageUrl(featured, featured.images[0], true)}
                srcSet={`${newsImageUrl(featured, featured.images[0], true)} 640w, ${newsImageUrl(featured, featured.images[0])} 1440w`}
                sizes="(max-width: 1023px) 92vw, 520px"
                alt={featured.images[0].alt}
                width={640}
                height={427}
                fetchPriority="high"
              />
              <span className="hero-news__featured-body">
                <span className="hero-news__meta">
                  <span className="hero-news__kicker">{newsKicker(featured, language)}</span>
                  <time dateTime={featured.date}>{formatNewsDate(featured.date, language)}</time>
                </span>
                <span className="hero-news__featured-title">{newsText(featured, language).title}</span>
                <span className="hero-news__more">
                  {t('news.readMore')} <FaArrowRight aria-hidden />
                </span>
              </span>
            </a>

            {rest.length > 0 && (
              <ul className="hero-news__list">
                {rest.map((item) => (
                  <li key={item.slug}>
                    <a className="hero-news__item" href={`/vesti/${item.slug}/`}>
                      <img
                        src={newsImageUrl(item, item.images[0], true)}
                        alt=""
                        loading="lazy"
                        width={640}
                        height={427}
                      />
                      <span className="hero-news__item-body">
                        <time dateTime={item.date}>{formatNewsDate(item.date, language)}</time>
                        <span className="hero-news__item-title">{newsText(item, language).title}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </motion.aside>
        )}

        <motion.div className="hero__stats" {...enter(0.7)}>
          <div className="hero__stat">
            <FaUsers className="hero__stat-icon" />
            <div className="hero__stat-content">
              <span className="hero__stat-number">100+</span>
              <span className="hero__stat-label">{t('hero.stats.activePlayers')}</span>
            </div>
          </div>
          <div className="hero__stat">
            <FaCalendarAlt className="hero__stat-icon" />
            <div className="hero__stat-content">
              <span className="hero__stat-number">U7–U14</span>
              <span className="hero__stat-label">{t('hero.stats.generations')}</span>
            </div>
          </div>
          <div className="hero__stat">
            <FaHeart className="hero__stat-icon" />
            <div className="hero__stat-content">
              <span className="hero__stat-number">∞</span>
              <span className="hero__stat-label">{t('hero.stats.smiles')}</span>
            </div>
          </div>
          <div className="hero__stat">
            <FaPlay className="hero__stat-icon" />
            <div className="hero__stat-content">
              <span className="hero__stat-number">20</span>
              <span className="hero__stat-label">{t('hero.stats.yearsExcellence')}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
